import React, { useMemo, useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DragDropContext, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot, DroppableProvided } from 'react-beautiful-dnd';
import { Button, Icon, Tag, Menu, MenuItem, Dialog, InputGroup, FormGroup, Intent, ContextMenu, Card, Elevation, Tooltip, Collapse, EditableText } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { formatDistanceToNow } from 'date-fns';
import { StrictModeDroppable } from '../common/StrictModeDroppable';
import './ConversationSidebarDnD.css';

interface Props {
  onSelect: (id: string) => void;
}

interface ConversationCardProps {
  conversation: any;
  active: boolean;
  onClick: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onFork?: () => void;
  onToggleVisibility?: () => void;
  onRename?: (newName: string) => void;
  currentUserId?: string;
}

interface FolderHeaderProps {
  folder: any;
  onRename?: () => void;
  onDelete?: () => void;
  conversationCount: number;
}

// Section header with count badge
const SectionHeader: React.FC<{ title: string; count: number; onAdd?: () => void }> = ({ title, count, onAdd }) => (
  <div className="conversation-section-header">
    <div className="section-title-group">
      <span className="section-title">{title}</span>
    </div>
    {onAdd && (
      <Button 
        icon={IconNames.PLUS} 
        minimal 
        small 
        className="section-add-btn"
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        title={`Create ${title.toLowerCase()} conversation`}
      />
    )}
  </div>
);

// Conversation card with metadata in tooltip and inline editing
const ConversationCard: React.FC<ConversationCardProps> = ({ 
  conversation, 
  active,
  onClick,
  onDelete,
  onDuplicate,
  onFork,
  onToggleVisibility,
  onRename,
  currentUserId
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(conversation.title || 'Untitled Conversation');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  
  // Determine if current user owns this conversation
  // Note: If we can't determine ownership (missing IDs), assume user is owner for safety
  const isOwner = !currentUserId || !conversation.userId || currentUserId === conversation.userId;
  
  console.log('[ConversationCard] Ownership check:', {
    conversationId: conversation.id,
    conversationUserId: conversation.userId,
    currentUserId,
    isOwner,
    visibility: conversation.visibility
  });

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPopoverOpen(false);
    setIsEditing(true);
    setEditedTitle(conversation.title || 'Untitled Conversation');
  };

  const handleRenameConfirm = () => {
    if (editedTitle.trim() && editedTitle !== conversation.title) {
      onRename?.(editedTitle);
    }
    setIsEditing(false);
  };

  const handleRenameCancel = () => {
    setEditedTitle(conversation.title || 'Untitled Conversation');
    setIsEditing(false);
  };

  // Build tooltip content
  const tooltipContent = (
    <div style={{ fontSize: '11px', lineHeight: '1.5', maxWidth: '300px' }}>
      {conversation.creator && (
        <div><strong>Creator:</strong> {conversation.creator.name || conversation.creator.email}</div>
      )}
      {conversation.lastMessageAt && (
        <div><strong>Last message:</strong> {formatDistanceToNow(new Date(conversation.lastMessageAt), { addSuffix: true })}</div>
      )}
      {conversation.currentModel && (
        <div><strong>Model:</strong> {conversation.currentModel}</div>
      )}
      {conversation.messageCount > 0 && (
        <div><strong>Messages:</strong> {conversation.messageCount}</div>
      )}
      {conversation.totalTokenCount > 0 && (
        <div><strong>Tokens:</strong> {conversation.totalTokenCount.toLocaleString()}</div>
      )}
      {conversation.modelsUsed && conversation.modelsUsed.length > 0 && (
        <div><strong>Models used:</strong> {conversation.modelsUsed.join(', ')}</div>
      )}
    </div>
  );

  const actionMenu = (
    <Menu>
      {/* Rename - only for owners */}
      {isOwner && (
        <MenuItem 
          icon={IconNames.EDIT} 
          text="Rename" 
          onClick={handleRenameClick}
        />
      )}
      
      {/* Duplicate - only for owners */}
      {isOwner && (
        <MenuItem 
          icon={IconNames.DUPLICATE} 
          text="Duplicate" 
          onClick={(e) => {
            e.stopPropagation();
            setIsPopoverOpen(false);
            onDuplicate?.();
          }}
        />
      )}
      
      {/* Fork - only for shared conversations not owned by user */}
      {!isOwner && conversation.visibility === 'shared' && (
        <MenuItem 
          icon={IconNames.FORK} 
          text="Fork Conversation"
          onClick={(e) => {
            e.stopPropagation();
            setIsPopoverOpen(false);
            onFork?.();
          }}
        />
      )}
      
      {/* Toggle visibility - only for owners */}
      {isOwner && (
        <MenuItem 
          icon={conversation.visibility === 'shared' ? IconNames.LOCK : IconNames.UNLOCK} 
          text={conversation.visibility === 'shared' ? 'Make Private' : 'Make Shared'}
          onClick={(e) => {
            e.stopPropagation();
            setIsPopoverOpen(false);
            onToggleVisibility?.();
          }}
        />
      )}
      
      {/* Delete - only for owners */}
      {isOwner && (
        <MenuItem 
          icon={IconNames.TRASH} 
          text="Delete" 
          intent={Intent.DANGER}
          onClick={(e) => {
            e.stopPropagation();
            setIsPopoverOpen(false);
            onDelete?.();
          }}
        />
      )}
    </Menu>
  );

        return (
          <Tooltip content={tooltipContent} placement="right" hoverOpenDelay={500} disabled={isEditing || isPopoverOpen}>
            <div className={`conversation-card ${active ? 'active' : ''}`}>
              {isEditing ? (
                <EditableText
                  value={editedTitle}
                  onChange={setEditedTitle}
                  onConfirm={handleRenameConfirm}
                  onCancel={handleRenameCancel}
                  selectAllOnFocus
                  placeholder="Conversation title..."
                  className="conversation-title-edit"
                />
              ) : (
                <div className="conversation-title" onClick={onClick}>
                  {conversation.title || 'Untitled Conversation'}
                </div>
              )}
              <Popover2
                content={actionMenu}
                placement="right-start"
                isOpen={isPopoverOpen}
                onInteraction={(nextState) => setIsPopoverOpen(nextState)}
              >
                <Button
                  icon={IconNames.MORE}
                  minimal
                  small
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPopoverOpen(!isPopoverOpen);
                  }}
                  className="conversation-card-actions"
                />
              </Popover2>
            </div>
          </Tooltip>
  );
};

// Folder header with actions
const FolderHeader: React.FC<FolderHeaderProps> = ({ folder, onRename, onDelete, conversationCount }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menu = (
    <Menu>
      <MenuItem 
        icon={IconNames.EDIT} 
        text="Rename" 
        onClick={() => {
          setIsMenuOpen(false);
          onRename?.();
        }} 
      />
      <MenuItem 
        icon={IconNames.TRASH} 
        text="Delete" 
        intent={Intent.DANGER}
        onClick={() => {
          setIsMenuOpen(false);
          onDelete?.();
        }}
      />
    </Menu>
  );

  return (
    <div className="folder-header">
      <Icon 
        icon={IconNames.FOLDER_OPEN} 
        size={14}
        style={{ opacity: 0.5, color: '#6b7280' }}
      />
      <span className="folder-name">{folder.name}</span>
      <Popover2
        content={menu}
        placement="right-start"
        isOpen={isMenuOpen}
        onInteraction={(nextState) => setIsMenuOpen(nextState)}
        minimal
      >
        <Button
          icon={IconNames.MORE}
          minimal
          small
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
          style={{ marginLeft: 'auto', opacity: 0 }}
          className="folder-actions"
        />
      </Popover2>
    </div>
  );
};

// Main component
const ConversationSidebarDnD: React.FC<Props> = observer(({ onSelect }) => {
  const appStore = useAppStore();
  const { chat: chatStore } = appStore;
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderType, setFolderType] = useState<'private' | 'shared'>('private');
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; id: string; name: string }>({ 
    open: false, 
    id: '', 
    name: '' 
  });

  // Filter conversations (use conversationsArray since conversations is a Map in MST)
  // Note: Use .length to trigger re-computation when items are added/removed
  // Folders disabled for now - show all conversations regardless of folderId
  const privateConvos = useMemo(() => 
    chatStore.conversationsArray.filter((c) => c.visibility !== 'shared'), 
    [chatStore.conversationsArray.length]
  );
  
  const sharedConvos = useMemo(() => 
    chatStore.conversationsArray.filter((c) => c.visibility === 'shared'), 
    [chatStore.conversationsArray.length]
  );

  // Handle drag end
  const onDragEnd = useCallback(async (result: DropResult) => {
    console.log('[ConversationSidebarDnD] ========== DRAG END ==========');
    console.log('[ConversationSidebarDnD] Full result:', JSON.stringify(result, null, 2));
    console.log('[ConversationSidebarDnD] Draggable ID:', result.draggableId);
    console.log('[ConversationSidebarDnD] Source:', {
      droppableId: result.source?.droppableId,
      index: result.source?.index
    });
    console.log('[ConversationSidebarDnD] Destination:', {
      droppableId: result.destination?.droppableId,
      index: result.destination?.index
    });
    
    if (!result.destination) {
      console.log('[ConversationSidebarDnD] ❌ No destination, cancelling drag');
      return;
    }
    
    const { draggableId, source, destination } = result;
    
    // Don't do anything if dropped in same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('[ConversationSidebarDnD] ⚠️ Same position, no change needed');
      return;
    }

    const conversation = chatStore.conversationsArray.find((c) => c.id === draggableId);
    if (!conversation) {
      console.error('[ConversationSidebarDnD] ❌ Conversation not found:', draggableId);
      console.error('[ConversationSidebarDnD] Available conversations:', chatStore.conversationsArray.map(c => ({id: c.id, title: c.title})));
      return;
    }

    console.log('[ConversationSidebarDnD] ✅ Found conversation:', {
      id: conversation.id,
      title: conversation.title,
      currentVisibility: conversation.visibility,
      currentFolderId: conversation.folderId
    });
    console.log('[ConversationSidebarDnD] 📍 Moving from:', source.droppableId, 'index:', source.index);
    console.log('[ConversationSidebarDnD] 📍 Moving to:', destination.droppableId, 'index:', destination.index);
    console.log('[ConversationSidebarDnD] 🔍 All folders:', chatStore.foldersArray.map(f => ({ id: f.id, name: f.name, type: f.type })));

    try {
      // Determine source and destination contexts
      const isFromFolder = source.droppableId.startsWith('folder:');
      const isToFolder = destination.droppableId.startsWith('folder:');
      const sourceFolderId = isFromFolder ? source.droppableId.replace('folder:', '') : null;
      const destFolderId = isToFolder ? destination.droppableId.replace('folder:', '') : null;

      console.log('[ConversationSidebarDnD] 🔍 Context analysis:', {
        isFromFolder,
        isToFolder,
        sourceFolderId,
        destFolderId
      });

      // Handle all drag scenarios
      if (isToFolder) {
        // SCENARIO: Dragging TO a folder (from anywhere)
        console.log('[ConversationSidebarDnD] 📂 SCENARIO: Moving to folder:', destFolderId);
        
        // Find the destination folder to get its type
        const destFolder = chatStore.foldersArray.find((f) => f.id === destFolderId);
        console.log('[ConversationSidebarDnD] Destination folder details:', destFolder);
        
        if (destFolder) {
          // If moving between different folders
          if (isFromFolder && sourceFolderId !== destFolderId) {
            console.log('[ConversationSidebarDnD] 🔄 Moving between folders');
          }
          
          console.log('[ConversationSidebarDnD] 🎯 Updating conversation with:', { 
            folder_id: destFolderId,
            visibility: destFolder.type
          });
          
          // Move to folder and set visibility based on folder type
          await chatStore.updateConversation(conversation.id, { 
            folder_id: destFolderId,
            visibility: destFolder.type // Set visibility to match folder's type
          });
          
          console.log('[ConversationSidebarDnD] ✅ Successfully moved to folder');
          // Show success toast
          const ToastService = (await import('../../services/ToastService')).ToastService;
          ToastService.success(`Moved "${conversation.title}" to folder "${destFolder.name}"`);
        } else {
          console.error('[ConversationSidebarDnD] ❌ Destination folder not found!');
          const ToastService = (await import('../../services/ToastService')).ToastService;
          ToastService.error('Folder not found');
        }
      } 
      else if (destination.droppableId === 'private' || destination.droppableId === 'shared') {
        // SCENARIO: Dragging to root private/shared sections
        const newVisibility = destination.droppableId as 'private' | 'shared';
        console.log('[ConversationSidebarDnD] 🏠 SCENARIO: Moving to root section:', newVisibility);
        
        if (isFromFolder) {
          // Moving OUT of a folder to root
          console.log('[ConversationSidebarDnD] 📤 Moving OUT of folder to', newVisibility, 'root');
          console.log('[ConversationSidebarDnD] 🎯 Updating conversation with:', { 
            visibility: newVisibility,
            folder_id: null
          });
          
          await chatStore.updateConversation(conversation.id, { 
            visibility: newVisibility,
            folder_id: null // Remove from folder (use snake_case for backend)
          });
          
          console.log('[ConversationSidebarDnD] ✅ Successfully moved to root');
          // Show success toast
          const ToastService = (await import('../../services/ToastService')).ToastService;
          ToastService.success(`Moved "${conversation.title}" to ${newVisibility}`);
        } else {
          // Moving between root sections or reordering within same section
          if (source.droppableId !== destination.droppableId) {
            console.log('[ConversationSidebarDnD] 🔄 Changing visibility from', source.droppableId, 'to:', newVisibility);
            console.log('[ConversationSidebarDnD] 🎯 Updating conversation with:', { 
              visibility: newVisibility,
              folder_id: null
            });
            
            await chatStore.updateConversation(conversation.id, { 
              visibility: newVisibility,
              folder_id: null
            });
            
            console.log('[ConversationSidebarDnD] ✅ Successfully changed visibility');
            // Show success toast
            const ToastService = (await import('../../services/ToastService')).ToastService;
            ToastService.success(`Moved "${conversation.title}" to ${newVisibility}`);
          } else {
            // Same section - just reordering
            console.log('[ConversationSidebarDnD] 📐 Reordering within', newVisibility, 'section');
            console.log('[ConversationSidebarDnD] 📍 From index:', source.index, 'To index:', destination.index);
            // TODO: Implement position/order persistence if needed
            // await chatStore.reorderConversation(conversation.id, destination.index);
          }
        }
      }
      console.log('[ConversationSidebarDnD] ✅ Successfully completed drag operation');
      console.log('[ConversationSidebarDnD] ========== END DRAG ==========\n');
    } catch (error) {
      console.error('[ConversationSidebarDnD] ❌❌❌ Error updating conversation:', error);
      console.error('[ConversationSidebarDnD] Error details:', {
        message: error.message,
        stack: error.stack,
        conversation,
        source,
        destination
      });
      // Show error toast
      const ToastService = (await import('../../services/ToastService')).ToastService;
      ToastService.error(`Failed to move conversation: ${error.message}`);
    }
  }, [chatStore]);

  // Create new conversation
  const handleCreateConversation = useCallback(async (visibility: 'private' | 'shared') => {
    const conv = await chatStore.createConversation('New Conversation', visibility);
    if (conv?.id) onSelect(conv.id);
  }, [chatStore, onSelect]);

  // Create folder
  const handleCreateFolder = useCallback(async () => {
    if (!folderName.trim()) return;
    await         chatStore.createFolder(
          folderName,
          folderType
        );
    setCreateFolderDialog(false);
    setFolderName('');
  }, [chatStore, folderName, folderType]);

  // Delete conversation
  const handleDeleteConversation = useCallback(async (id: string) => {
    if (confirm('Delete this conversation?')) {
      await chatStore.deleteConversation(id);
    }
  }, [chatStore]);

  // Duplicate conversation
  const handleDuplicateConversation = useCallback(async (id: string) => {
    const conv = chatStore.conversations.get(id);
    if (conv) {
      const newConv = await chatStore.duplicateConversation(id);
      if (newConv?.id) onSelect(newConv.id);
    }
  }, [chatStore, onSelect]);
  
  // Fork conversation (creates a new copy owned by current user)
  const handleForkConversation = useCallback(async (id: string) => {
    const conv = chatStore.conversations.get(id);
    if (conv) {
      const newConv = await chatStore.forkConversation(id);
      if (newConv?.id) onSelect(newConv.id);
    }
  }, [chatStore, onSelect]);

  // Toggle visibility
  const handleToggleVisibility = useCallback(async (id: string) => {
    const conv = chatStore.conversations.get(id);
    if (conv) {
      await chatStore.updateConversation(id, {
        visibility: conv.visibility === 'shared' ? 'private' : 'shared'
      });
    }
  }, [chatStore]);

  // Rename folder
  const handleRenameFolder = useCallback(async () => {
    if (!renameDialog.name.trim()) return;
    await chatStore.updateFolder(renameDialog.id, { name: renameDialog.name });
    setRenameDialog({ open: false, id: '', name: '' });
  }, [chatStore, renameDialog]);

  // Delete folder
  const handleDeleteFolder = useCallback(async (id: string) => {
    if (confirm('Delete this folder? Conversations will be moved to root.')) {
      await chatStore.deleteFolder(id);
    }
  }, [chatStore]);

  return (
    <div className="conversation-sidebar-dnd">
      {/* Header with search */}
      <div className="sidebar-header">
        <InputGroup
          leftIcon={IconNames.SEARCH}
          placeholder="Search conversations..."
          value={chatStore.searchQuery}
          onChange={(e) => chatStore.setSearchQuery(e.target.value)}
          className="conversation-search-input"
        />
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        {/* Shared conversations section - shown first */}
        <StrictModeDroppable droppableId="shared">
          {(provided: DroppableProvided, snapshot) => (
            <div 
              ref={provided.innerRef} 
              {...provided.droppableProps} 
              className={`conversation-section ${snapshot.isDraggingOver ? 'drag-over-shared' : ''}`}
            >
              <SectionHeader 
                title="Shared" 
                count={sharedConvos.length}
                onAdd={() => handleCreateConversation('shared')}
              />
              <div className="conversation-list">
                {sharedConvos.map((conv, index) => (
                  <Draggable key={conv.id} draggableId={conv.id} index={index}>
                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={snapshot.isDragging ? 'dragging' : ''}
                      >
                        <ConversationCard
                          conversation={conv}
                          active={chatStore.activeConversationId === conv.id}
                          onClick={() => onSelect(conv.id)}
                          onDelete={() => handleDeleteConversation(conv.id)}
                          onDuplicate={() => handleDuplicateConversation(conv.id)}
                          onFork={() => handleForkConversation(conv.id)}
                          onToggleVisibility={() => handleToggleVisibility(conv.id)}
                          onRename={(newTitle) => chatStore.updateConversation(conv.id, { title: newTitle })}
                          currentUserId={appStore.currentUser?.id}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            </div>
          )}
        </StrictModeDroppable>

        {/* Private conversations section */}
        <StrictModeDroppable droppableId="private">
          {(provided: DroppableProvided, snapshot) => (
            <div 
              ref={provided.innerRef} 
              {...provided.droppableProps} 
              className={`conversation-section ${snapshot.isDraggingOver ? 'drag-over-private' : ''}`}
            >
              <SectionHeader 
                title="Private" 
                count={privateConvos.length} 
                onAdd={() => handleCreateConversation('private')}
              />
              <div className="conversation-list">
                {privateConvos.map((conv, index) => (
                  <Draggable key={conv.id} draggableId={conv.id} index={index}>
                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={snapshot.isDragging ? 'dragging' : ''}
                      >
                        <ConversationCard
                          conversation={conv}
                          active={chatStore.activeConversationId === conv.id}
                          onClick={() => onSelect(conv.id)}
                          onDelete={() => handleDeleteConversation(conv.id)}
                          onDuplicate={() => handleDuplicateConversation(conv.id)}
                          onFork={() => handleForkConversation(conv.id)}
                          onToggleVisibility={() => handleToggleVisibility(conv.id)}
                          onRename={(newTitle) => chatStore.updateConversation(conv.id, { title: newTitle })}
                          currentUserId={appStore.currentUser?.id}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            </div>
          )}
        </StrictModeDroppable>
      </DragDropContext>

      {/* Create Folder Dialog */}
      <Dialog
        isOpen={createFolderDialog}
        onClose={() => setCreateFolderDialog(false)}
        title="Create Folder"
      >
        <div className="bp5-dialog-body">
          <FormGroup label="Folder Name">
            <InputGroup
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name..."
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Type">
            <div className="bp5-control-group">
              <Button
                text="Private"
                icon={IconNames.LOCK}
                active={folderType === 'private'}
                onClick={() => setFolderType('private')}
              />
              <Button
                text="Shared"
                icon={IconNames.PEOPLE}
                active={folderType === 'shared'}
                onClick={() => setFolderType('shared')}
              />
            </div>
          </FormGroup>
        </div>
        <div className="bp5-dialog-footer">
          <div className="bp5-dialog-footer-actions">
            <Button onClick={() => setCreateFolderDialog(false)}>Cancel</Button>
            <Button 
              intent={Intent.PRIMARY} 
              onClick={handleCreateFolder}
              disabled={!folderName.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog
        isOpen={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, id: '', name: '' })}
        title="Rename Folder"
      >
        <div className="bp5-dialog-body">
          <FormGroup label="Folder Name">
            <InputGroup
              value={renameDialog.name}
              onChange={(e) => setRenameDialog({ ...renameDialog, name: e.target.value })}
              placeholder="Enter new name..."
              autoFocus
            />
          </FormGroup>
        </div>
        <div className="bp5-dialog-footer">
          <div className="bp5-dialog-footer-actions">
            <Button onClick={() => setRenameDialog({ open: false, id: '', name: '' })}>
              Cancel
            </Button>
            <Button 
              intent={Intent.PRIMARY} 
              onClick={handleRenameFolder}
              disabled={!renameDialog.name.trim()}
            >
              Rename
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
});

export default ConversationSidebarDnD;